/**
 * Utilidades para formateo de fechas UTC a hora local
 * 
 * Este módulo proporciona funciones para detectar y convertir automáticamente
 * fechas en formato UTC a la hora local del usuario.
 */

/**
 * Formatea texto que contiene fechas UTC a hora local
 * 
 * Detecta fechas en formato ISO 8601 (YYYY-MM-DDTHH:MM:SS+00:00) o
 * formato DD/MM/YYYY HH:MM UTC dentro de un texto y las convierte
 * automáticamente a la hora local del navegador.
 * 
 * @param {string} text - Texto que puede contener fechas UTC
 * @returns {string} - Texto con fechas convertidas a hora local
 * 
 * @example
 * // Backend envía: "Cita el 2025-10-18T14:30:00+00:00"
 * // Frontend muestra: "Cita el 18/Oct/2025 09:30" (para UTC-5)
 * formatTextWithDates("Cita el 2025-10-18T14:30:00+00:00")
 */
export function formatTextWithDates(text) {
    if (!text) return '';

    // Patrón para detectar fechas en formato ISO 8601: YYYY-MM-DDTHH:MM:SS+00:00
    // o formato DD/MM/YYYY HH:MM UTC
    const utcDatePattern = /(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2})\s+UTC|(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2})/g;

    return text.replace(utcDatePattern, (match, ddmmyyyy, iso8601) => {
        try {
            let date;

            if (iso8601) {
                // Formato ISO 8601: 2025-10-18T14:30:00+00:00
                date = new Date(iso8601);
            } else if (ddmmyyyy) {
                // Formato DD/MM/YYYY HH:MM UTC
                const [datePart, timePart] = ddmmyyyy.split(' ');
                const [day, month, year] = datePart.split('/');
                const [hours, minutes] = timePart.split(':');
                date = new Date(Date.UTC(year, month - 1, day, hours, minutes));
            }

            if (date && !isNaN(date.getTime())) {
                // Formatear a hora local del usuario
                const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
                    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
                ];
                const localMonth = monthNames[date.getMonth()];
                const localDay = String(date.getDate()).padStart(2, '0');
                const localYear = date.getFullYear();
                const localHours = String(date.getHours()).padStart(2, '0');
                const localMinutes = String(date.getMinutes()).padStart(2, '0');

                // Retornar fecha formateada en hora local
                return `${localDay}/${localMonth}/${localYear} ${localHours}:${localMinutes}`;
            }
        } catch (e) {
            console.error('Error parsing date:', e);
        }

        // Si no se puede parsear, retornar el texto original
        return match;
    });
}

/**
 * Formatea una fecha ISO 8601 UTC a hora local en formato legible
 * 
 * @param {string} isoDate - Fecha en formato ISO 8601 UTC (YYYY-MM-DDTHH:MM:SS+00:00)
 * @param {boolean} includeTime - Si incluir la hora (default: true)
 * @returns {string} - Fecha formateada en hora local
 * 
 * @example
 * formatDate("2025-10-18T14:30:00+00:00") 
 * // Returns: "18/Oct/2025 09:30" (para UTC-5)
 * 
 * formatDate("2025-10-18T14:30:00+00:00", false)
 * // Returns: "18/Oct/2025"
 */
export function formatDate(isoDate, includeTime = true) {
    if (!isoDate) return '';

    try {
        const date = new Date(isoDate);

        if (isNaN(date.getTime())) {
            console.error('Invalid date:', isoDate);
            return isoDate;
        }

        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
            'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
        ];
        const localMonth = monthNames[date.getMonth()];
        const localDay = String(date.getDate()).padStart(2, '0');

        if (!includeTime) {
            return `${localDay} ${localMonth}`;
        }

        const localHours = String(date.getHours()).padStart(2, '0');
        const localMinutes = String(date.getMinutes()).padStart(2, '0');

        return `${localDay} ${localMonth} ${localHours}:${localMinutes}`;
    } catch (e) {
        console.error('Error formatting date:', e);
        return isoDate;
    }
}

/**
 * Convierte una fecha ISO 8601 UTC a un objeto Date en hora local
 * 
 * @param {string} isoDate - Fecha en formato ISO 8601 UTC
 * @returns {Date|null} - Objeto Date en hora local o null si es inválido
 */
export function parseUTCDate(isoDate) {
    if (!isoDate) return null;

    try {
        const date = new Date(isoDate);
        return isNaN(date.getTime()) ? null : date;
    } catch (e) {
        console.error('Error parsing UTC date:', e);
        return null;
    }
}